import { Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { Facebook, Instagram, Youtube, Globe } from 'lucide-react';
import { useAuth } from '../../features/auth/AuthContext';
import { getBlogPosts } from '../services/blogService';
import { sampleBlogPosts } from '../data/sampleData';
import { BlogPost } from '../types';

const PHONE_NUMBERS = [
	{ flag: '🇮🇳', number: '+91 9645310500' },
	{ flag: '🇮🇳', number: '+91 9496913122' },
	{ flag: '🇺🇸', number: '+1 6238452598' },
	{ flag: '🇺🇸', number: '+1 6024456320' },
];

// Instagram and YouTube point at the main site as a placeholder until the real
// profile URLs are available — they must not link to the retired WordPress site.
const SOCIAL_LINKS = [
	{ icon: Facebook, href: 'https://www.facebook.com/profile.php?id=61550337686466', label: 'Facebook' },
	{ icon: Instagram, href: 'https://pop3itechnologies.com', label: 'Instagram' },
	{ icon: Youtube, href: 'https://pop3itechnologies.com', label: 'YouTube' },
];

export default function Footer() {
	const { user } = useAuth();
	const [posts, setPosts] = useState<BlogPost[]>(sampleBlogPosts);

	useEffect(() => {
		getBlogPosts()
			.then((firestorePosts) => {
				if (firestorePosts.length > 0) setPosts(firestorePosts);
			})
			.catch(() => {
				// Keep sample posts on failure
			});
	}, []);

	const uniquePosts = posts.filter(
		(post, index) => posts.findIndex((p) => p.title === post.title) === index
	);
	const recentPosts = uniquePosts.slice(0, 5);

	return (
		<footer className="border-t bg-gray-50">
			<div className="mx-auto max-w-6xl px-4 py-12">
				<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10 mb-8">
					{/* Logo / Contact / Social */}
					<div>
						<Link to="/" className="inline-block mb-3">
							<img src="/logo.png" alt="POP3i Properties" className="h-16 w-auto object-contain" />
						</Link>
						<p className="text-sm font-semibold text-gray-800 mb-3">NRI's Ultimate Real-estate Portal</p>
						<a
							href="https://pop3itechnologies.com"
							target="_blank"
							rel="noopener noreferrer"
							className="flex items-center gap-2 text-sm text-gray-600 hover:text-brand-700 mb-6"
						>
							<Globe className="h-4 w-4" />
							www.pop3itechnologies.com
						</a>

						<h3 className="text-sm font-bold tracking-wide text-gray-900 mb-3">CONTACT US</h3>
						<div className="space-y-2 mb-6">
							{PHONE_NUMBERS.map((p) => (
								<div key={p.number} className="flex items-center gap-2 text-sm text-gray-600">
									<span aria-hidden="true">{p.flag}</span>
									<span>{p.number}</span>
								</div>
							))}
						</div>

						<div className="flex items-center gap-3">
							{SOCIAL_LINKS.map(({ icon: Icon, href, label }) => (
								<a
									key={label}
									href={href}
									target="_blank"
									rel="noopener noreferrer"
									aria-label={label}
									className="w-9 h-9 rounded-full bg-gray-900 text-white flex items-center justify-center hover:bg-brand-600 transition-colors"
								>
									<Icon className="h-4 w-4" />
								</a>
							))}
						</div>
					</div>

					{/* About Us Section */}
					<div>
						<h3 className="text-sm font-bold tracking-wide text-gray-900 mb-4">ABOUT US</h3>
						<div className="text-sm text-gray-600 space-y-3">
							<p>Welcome to the exclusive real estate platform catering to NRIs and OCIs, designed specifically for your diverse property needs in India.</p>
							<p>Discover a wide range of offerings, from luxurious homes to lucrative investments, all tailored to your preferences. Browse listings, access detailed property descriptions, and take virtual tours with ease. Our dedicated team ensures seamless transactions, prioritizing transparency and legal compliance.</p>
							<p>Join us on a journey of real estate exploration, where every step is a joyful experience. Let us be your partner in turning your real estate vision into reality in India's dynamic property market.</p>
						</div>
					</div>

					{/* Quick Links Section */}
					<div>
						<h3 className="text-sm font-bold tracking-wide text-gray-900 mb-4">QUICK LINKS</h3>
						<div className="flex flex-col text-sm">
							<Link to="/emi-calculator" className="text-gray-600 hover:text-brand-700 py-2 border-b border-gray-200">
								EMI – Calculator
							</Link>
							<Link to="/sales-and-marketing-services" className="text-gray-600 hover:text-brand-700 py-2 border-b border-gray-200">
								Sales and Marketing Services
							</Link>
							<Link to="/professional-services" className="text-gray-600 hover:text-brand-700 py-2 border-b border-gray-200">
								Professional Services
							</Link>
							<Link to="/buyers-checklist" className="text-gray-600 hover:text-brand-700 py-2 border-b border-gray-200">
								Buyers Checklist
							</Link>
							<Link to="/documents-required-for-building-permit" className="text-gray-600 hover:text-brand-700 py-2 border-b border-gray-200">
								Documents required while applying for a building permit
							</Link>
							<Link to="/essential-documents-for-nri-sellers-in-kerala" className="text-gray-600 hover:text-brand-700 py-2 border-b border-gray-200">
								Essential Documents for NRI Sellers/ Buyers in Kerala
							</Link>
							<Link to="/terms-and-conditions" className="text-gray-600 hover:text-brand-700 py-2 border-b border-gray-200 last:border-b-0">
								Terms and Conditions
							</Link>
							{user && (
								<Link to="/list-property" className="text-brand-600 hover:text-brand-800 font-semibold py-2 border-b border-gray-200 last:border-b-0">
									List Your Property
								</Link>
							)}
						</div>
					</div>

					{/* Blogs Section */}
					<div>
						<h3 className="text-sm font-bold tracking-wide text-gray-900 mb-4">BLOGS</h3>
						<div className="flex flex-col text-sm">
							{recentPosts.map((post) => (
								<Link
									key={post.id}
									to="/blog"
									className="py-2 border-b border-gray-200 last:border-b-0 group"
								>
									<div className="text-gray-800 font-medium group-hover:text-brand-700 line-clamp-2">
										{post.title}
									</div>
									<p className="text-gray-500 text-xs mt-1 line-clamp-2">{post.excerpt}</p>
								</Link>
							))}
						</div>
					</div>
				</div>

				{/* Copyright Section */}
				<div className="border-t pt-6 text-sm text-gray-600">
					<div>© 2026 POP3i Properties.</div>
				</div>
			</div>
		</footer>
	);
}
